/*
 * Diagnostics for PDF viewer failures in locked-down browsers.
 *
 * Safe Exam Browser has no devtools and no console, so a `console.error(e)` is
 * unreachable there: every failure looks like the same generic "an error
 * occurred" message. That is why issue #785 went through several rounds of
 * blind fixes. These helpers let the viewer show the real error on screen.
 *
 * Details are only rendered when explicitly asked for — students must never see
 * a stack trace — via `?pdfDebug=1` on the URL, or `localStorage.pdfDebug = "1"`
 * for a build already running inside SEB.
 */

const STORAGE_KEY = "pdfDebug"

export const isDebugEnabled = (): boolean => {
    try {
        if (typeof window === "undefined") return false
        if (new URLSearchParams(window.location.search).has(STORAGE_KEY)) return true
        return window.localStorage.getItem(STORAGE_KEY) === "1"
    } catch {
        // Private mode / blocked storage must not break the viewer.
        return false
    }
}

/*
 * Failures that happen before React can catch them — a worker that fails to
 * load or parse, an uncaught async rejection inside pdf.js — land here so the
 * error panel can show them alongside the caught error.
 *
 * The store is a global rather than a module binding because pdfjsSetup.mjs
 * writes to it too, and webpack resolves .mjs fullySpecified — it cannot import
 * this .ts module by a bare "./diagnostics" specifier.
 */
const STORE_KEY = "__pdfViewerDiagnostics"

const store = (): string[] => {
    const g: any = globalThis as any
    if (!Array.isArray(g[STORE_KEY])) g[STORE_KEY] = []
    return g[STORE_KEY]
}

export const recordDiagnostic = (message: string) => {
    const messages = store()
    // Bounded: a failing worker can fire repeatedly.
    if (messages.length < 20) messages.push(message)
}

export const takeDiagnostics = (): string[] => [...store()]

export const describeError = (error: any): string => {
    if (!error) return "Unknown error (no error object)"

    const parts = [
        `${error.name || "Error"}: ${error.message || String(error)}`,
        // pdf.js attaches these on its own error subclasses; they say far more
        // than the message does (e.g. status 0 = request blocked, not 404).
        error.status !== undefined ? `status: ${error.status}` : null,
        error.details ? `details: ${error.details}` : null,
        error.stack ? `\n${error.stack}` : null,
    ]

    return parts.filter(Boolean).join("\n")
}

export const buildErrorReport = (error: any): string => {
    const crumbs = takeDiagnostics()

    return [
        describeError(error),
        crumbs.length ? `\n--- earlier failures ---\n${crumbs.join("\n")}` : null,
        `\n--- environment ---\nUA: ${typeof navigator !== "undefined" ? navigator.userAgent : "n/a"}`,
    ].filter(Boolean).join("\n")
}
